import { addonApi } from 'apiConnector/addon';
import { isHttpTimeoutError } from 'epic/apiErrorEpicHelper';
import { tabRemoved } from 'epic/workspace/common/tabs/tabsEpics/tabRemoved';
import type { MergeDeal } from 'models/forms/AddonFormResult';
import { ofType } from 'redux-observable';
import type { Observable, SchedulerLike } from 'rxjs';
import { merge, of } from 'rxjs';
import { catchError, filter, mergeMap, takeUntil, timeoutWith } from 'rxjs/operators';
import type { AddonFormRequestFailed, AddonFormRequestTimedOut } from 'state/addonForm/addonForm.action';
import {
  addonMergeableDealsLoaded,
  addonMergeableDealsLoadRequestFailed,
  addonMergeableDealsLoadTimedOut,
} from 'state/addonForm/addonForm.action';
import * as actionTypes from 'state/addonForm/addonForm.actionTypes';
import type { EpicWithThunk } from 'state/state.common';

export interface FetchMergedDealsApi {
  fetchMergedDeals(productId: string): Observable<ReadonlyArray<MergeDeal>>;
}

export function fetchMergedDealsEpic(timeoutInMillis: number = 60000, appScheduler?: SchedulerLike): EpicWithThunk {
  return (actions$, state$, { oldHttpClient }) => {
    const addonFormRequested$ = actions$.pipe(ofType(actionTypes.ADDON_FORM_REQUESTED));

    return addonFormRequested$.pipe(
      mergeMap(({ tabId, productId }) => {
        const tabRemoved$ = tabRemoved(actions$, tabId, state$);

        const formLoadingFailed$ = actions$.pipe(
          ofType('ADDON_FORM_REQUEST_FAILED'),
          filter((action: AddonFormRequestFailed) => action.tabId === tabId),
        );

        const formLoadingTimedout$ = actions$.pipe(
          ofType('ADDON_FORM_REQUEST_TIMED_OUT'),
          filter((action: AddonFormRequestTimedOut) => action.tabId === tabId),
        );

        return addonApi(oldHttpClient)
          .fetchMergedDeals(productId)
          .pipe(
            mergeMap(mergeableDeals => of(addonMergeableDealsLoaded(tabId, productId, mergeableDeals))),
            takeUntil(merge(tabRemoved$, formLoadingFailed$, formLoadingTimedout$)),
            catchError(error => {
              if (isHttpTimeoutError(error)) {
                return of(addonMergeableDealsLoadTimedOut(tabId, productId));
              }
              return of(addonMergeableDealsLoadRequestFailed(tabId, productId, error.message || 'default form failed'));
            }),
            timeoutWith(timeoutInMillis, of(addonMergeableDealsLoadTimedOut(tabId, productId)), appScheduler),
          );
      }),
    );
  };
}
